import { useCallback, useMemo, useReducer, useState } from 'react';
import { CreatePropertyContext, CreatePropertyContextActionsProps } from './CreatePropertyContext';
import { AddressProps, PropertyCommonsProps, ReactSimpleComponentProps, SimpleOwnerProps } from '@/custom-types';
import { CreatePropertyReducer } from '@/reducers';
import { CreatePropertyInput, CreateUnityInput, PropertyTypeOption } from '@/graphql/generated';

const emptyState: CreatePropertyInput = {
  addressLine: undefined,
  buildingNumber: '',
  cityId: '',
  countryId: '',
  name: '',
  ownerId: '',
  propertyType: PropertyTypeOption.Other,
  stateId: '',
  street: '',
  zipCode: '',
  unities: [],
  balcony: false,
  patio: false,
  commonBathroom: false,
  kitchen: false,
  elevator: false,
  commonAirConditioner: false,
  commonFan: false,
}

export const CreatePropertyContextProvider = ({ children }: ReactSimpleComponentProps) => {
  const [state, dispatch] = useReducer(CreatePropertyReducer, emptyState)
  const [isDisabled, setIsDisabled] = useState(false);
  const [ownerState, setOwnerState] = useState<SimpleOwnerProps>()
  const [pictures, setPictures] = useState<File[]>([])
  const [videos, setVideos] = useState<File[]>([])

  const setAddress = useCallback((address: AddressProps) => {
    dispatch({
      type: 'CREATE_PROPERTY-set_address',
      payload: address,
    })
  }, []);

  const setPropertyType = useCallback((propertyType: PropertyTypeOption) => {
    dispatch({
      type: 'CREATE_PROPERTY-set-type',
      payload: propertyType,
    })
  }, [])

  const setName = useCallback((name: string) => {
    dispatch({
      type: 'CREATE_PROPERTY-set_name',
      payload: name,
    })
  }, [])

  const setOwner = useCallback((owner: SimpleOwnerProps) => {
    setOwnerState(owner)
    dispatch({
      type: 'CREATE_PROPERTY-set_ownership',
      payload: owner?.user.id ?? '',
    })
  }, [])

  const setUnities = useCallback((unities: CreateUnityInput[]) => {
    dispatch({
      type: 'CREATE_PROPERTY-set_unities',
      payload: unities,
    })
  }, [])

  const setCommons = useCallback((commons: PropertyCommonsProps) => {
    dispatch({
      type: 'CREATE_PROPERTY-set_commons',
      payload: commons,
    })
  }, [])

  const setButtonDisabled = useCallback(() => setIsDisabled(true), []);
  const setButtonEnabled = useCallback(() => setIsDisabled(false), []);

  const handleSetPictures = useCallback((pictures: File[]) => {
    setPictures(pictures)
  }, []);

  const handleSetVideos = useCallback((videos: File[]) => {
    setVideos(videos)
  }, []);

  const actions = useMemo<Omit<CreatePropertyContextActionsProps, 'state' | 'buttonState' | 'ownerData' | 'pictures' | 'videos'>>(() => {
    return {
      setAddress,
      setPropertyType,
      setName,
      setOwner,
      setUnities,
      setCommons,
      setPictures: handleSetPictures,
      setVideos: handleSetVideos,
      setButtonDisabled,
      setButtonEnabled
    }
  }, [setAddress, setPropertyType, setName, setOwner, setUnities, setCommons, handleSetPictures, handleSetVideos, setButtonDisabled, setButtonEnabled])



  return (
    <CreatePropertyContext.Provider value={{
      ...actions,
      state,
      buttonState: isDisabled,
      ownerData: ownerState,
      pictures,
      videos,
    }}>
      {children}
    </CreatePropertyContext.Provider>
  )
}